<template>
  <div class="match-card" :class="{ 'match-card--expanded': expanded }">
    <div class="match-card-actions" v-if="!betResult">
      <div v-if="match.status" class="match-status-text">
        {{ match.status }}
      </div>
      <button v-if="canBet || isAlreadyBet"
              class="btn-bet-corner" 
              :class="{ 'btn-bet-corner--active': isAlreadyBet || !canBet }"
              @click.stop="$root.$emit('bet', match)">
        {{ (canBet && !isAlreadyBet) ? '下注' : '已投注' }}
      </button>
    </div>
    <div v-if="betResult" class="bet-result-corner" :class="'bet-status--' + betResult.status.toLowerCase()">
      <div class="bet-head">
        <span class="status-text">{{ translateStatus(betResult.status) }}</span>
        <span v-if="betResult.payout !== null" class="payout-text">
          {{ betResult.payout > 0 ? '+' : '' }}{{ betResult.payout }}
        </span>
      </div>
      <div class="bet-desc">{{ betResult.market_id }} • {{ translateSelection(betResult.selection_id) }}</div>
    </div>

    <!-- Main row (clickable to expand odds) -->
    <div class="match-card__body" @click="toggleExpand">
      <div class="match-card__favorite-container">
        <div class="match-time-container">
          <span v-if="showDate" class="match-date">{{ formatDate(match.match_date) }}</span>
          <span class="match-time">{{ formatTime(match.match_date) }}</span>
        </div>
        <!-- Popular indicator -->
        <div v-if="popularMode" class="popular-indicator">
          <svg
            class="star-icon star-icon--popular"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26 12,2"/>
          </svg>
          <span class="favorite-count">{{ match.favorite_count || 0 }}</span>
        </div>

        <!-- Star (favorite) toggle -->
        <button
          v-else
          class="btn-star"
          :class="{ 'btn-star--active': isFavorited }"
          @click.stop="toggleFavorite"
          :disabled="starLoading"
          :aria-label="isFavorited ? '取消關注' : '關注'"
        >
          <svg
            class="star-icon"
            viewBox="0 0 24 24"
            :fill="isFavorited ? 'currentColor' : 'none'"
            stroke="currentColor"
            stroke-width="2"
          >
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26 12,2"/>
          </svg>
        </button>
      </div>

      <!-- Teams -->
      <div class="match-card__teams">
        <div class="league-header">
          <img
            v-if="match.home_team && match.home_team.logo_url"
            :src="match.home_team.logo_url"
            class="league-logo"
            @error="handleLogoError"
            alt=""
          />
          <span class="league-name">{{ match.league }}</span>
          <img
            v-if="match.away_team && match.away_team.logo_url"
            :src="match.away_team.logo_url"
            class="league-logo"
            @error="handleLogoError"
            alt=""
          />
        </div>

        <div class="team team--home">
          <div class="team__info">
            <span class="team__name">{{ homeTeamName }}</span>
            <span class="team__rank" v-if="match.home_team && match.home_team.rank">[{{ match.home_team.rank }}]</span>
          </div>
        </div>

        <!-- Score / VS -->
        <div class="match-card__score">
          <template v-if="hasScore">
            <div class="score-full">{{ match.full_score }}</div>
            <div class="score-half">{{ match.half_score }}</div>
          </template>
          <template v-else>
            <span class="vs-text">VS</span>
          </template>
        </div>

        <div class="team team--away">
          <div class="team__info">
            <span class="team__name">{{ awayTeamName }}</span>
            <span class="team__rank" v-if="match.away_team && match.away_team.rank">[{{ match.away_team.rank }}]</span>
          </div>
        </div>

        <!-- Odds Preview Section -->
        <div class="match-card__odds-preview" v-if="match.first_odds">
          <!-- Column 1: Handicap -->
          <div class="odds-group">
            <div class="odds-group__header" v-if="isFirst">
              <span class="odds-header-label">主队</span>
              <span class="odds-header-label">让球</span>
              <span class="odds-header-label">客队</span>
            </div>
            <div class="odds-preview__item">
              <span class="odds-value">{{ fmt(match.first_odds.handicap_home) }}</span>
              <span class="odds-line">{{ formatHandicap(match.first_odds.handicap_line) }}</span>
              <span class="odds-value">{{ fmt(match.first_odds.handicap_away) }}</span>
            </div>
          </div>

          <span class="odds-divider">|</span>

          <!-- Column 2: 1x2 -->
          <div class="odds-group">
            <div class="odds-group__header" v-if="isFirst">
              <span class="odds-header-label">主胜</span>
              <span class="odds-header-label">和局</span>
              <span class="odds-header-label">客胜</span>
            </div>
            <div class="odds-preview__item">
              <span class="odds-value">{{ fmt(match.first_odds.odds_home_win) }}</span>
              <span class="odds-line">{{ fmt(match.first_odds.odds_draw) }}</span>
              <span class="odds-value">{{ fmt(match.first_odds.odds_away_win) }}</span>
            </div>
          </div>

          <span class="odds-divider">|</span>

          <!-- Column 3: Over/Under -->
          <div class="odds-group">
            <div class="odds-group__header" v-if="isFirst">
              <span class="odds-header-label">大球</span>
              <span class="odds-header-label">大小</span>
              <span class="odds-header-label">小球</span>
            </div>
            <div class="odds-preview__item">
              <span class="odds-value">{{ fmt(match.first_odds.goals_over) }}</span>
              <span class="odds-line">{{ formatGoalsLine(match.first_odds.goals_line) }}</span>
              <span class="odds-value">{{ fmt(match.first_odds.goals_under) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Expand arrow -->
      <div class="expand-arrow" :class="{ 'expand-arrow--open': expanded }">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="6,9 12,15 18,9"/>
        </svg>
      </div>
    </div>


    <!-- Expanded odds table -->
    <transition name="slide-down">
      <div class="match-card__odds-expanded" v-show="expanded">
        <odds-table :companies="oddsCompanies" :loading="oddsLoading" />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { oddsAPI } from '@/api/index'
import { formatHandicap, formatGoalsLine } from '@/utils/odds'
import { parseMatchTime, parseMatchDate } from '@/utils/time'
import OddsTable from './OddsTable.vue'

export default {
  name: 'MatchCard',
  components: { OddsTable },
  props: {
    match: {
      type: Object,
      required: true
    },
    isFirst: {
      type: Boolean,
      default: false
    },
    popularMode: {
      type: Boolean,
      default: false
    },
    showDate: {
      type: Boolean,
      default: false
    },
    betResult: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      expanded: false,
      oddsLoading: false,
      oddsCompanies: [],
      oddsLoaded: false,
      starLoading: false
    }
  },
  computed: {
    canBet() {
      return this.match ? !!this.match.is_bettable : false
    },
    isFavorited() {
      return this.$store.getters.isFavorited(this.match.analysis_id)
    },
    isAlreadyBet() {
      return this.$store.getters.hasBet(this.match.analysis_id)
    },
    hasScore() {
      return !!(this.match.full_score || this.match.half_score)
    },
    homeTeamName() {
      return this.match.home_team ? this.match.home_team.name : '—'
    },
    awayTeamName() {
      return this.match.away_team ? this.match.away_team.name : '—'
    }
  },
  methods: {
    formatHandicap,
    formatGoalsLine,
    ...mapActions(['addFavorite', 'removeFavorite']),

    translateStatus(s) {
      if (!s) return ''
      const m = { PENDING: '未開獎', WIN: '贏', LOSE: '輸', VOID: '退回本金', HALF_WIN: '贏半', HALF_LOSE: '輸半' }
      return m[s] || s
    },

    translateMarket(m) {
      if (!m) return ''
      const maps = { AH: '讓球盤', '1X2': '標準盤', OU: '大小球' }
      return maps[m] || m
    },

    translateSelection(s) {
      if (!s) return ''
      const m = { HOME: '主', AWAY: '客', DRAW: '和', OVER: '大', UNDER: '小' }
      return m[s] || s
    },

    formatTime(dateStr) {
      return parseMatchTime(dateStr)
    },

    formatDate(dateStr) {
      return parseMatchDate(dateStr)
    },

    fmt(val) {
      if (val === null || val === undefined) return '—'
      const n = Number(val)
      if (isNaN(n)) return val
      return n % 1 === 0 ? String(n) : n.toFixed(2).replace(/0+$/, '')
    },

    async toggleExpand() {
      this.expanded = !this.expanded
      if (this.expanded && !this.oddsLoaded) {
        await this.loadOdds()
      }
    },

    async loadOdds() {
      this.oddsLoading = true
      try {
        const res = await oddsAPI.getOdds(this.match.analysis_id)
        this.oddsCompanies = res.data.companies || []
        this.oddsLoaded = true
      } catch (e) {
        console.error('Failed to load odds', e)
        this.oddsCompanies = []
      } finally {
        this.oddsLoading = false
      }
    },

    async toggleFavorite() {
      if (this.starLoading) return
      this.starLoading = true
      try {
        if (this.isFavorited) {
          await this.removeFavorite(this.match.analysis_id)
        } else {
          await this.addFavorite(this.match.analysis_id)
        }
      } catch (e) {
        // Could show a toast here
        console.error('Failed to toggle favorite', e)
      } finally {
        this.starLoading = false
      }
    },

    handleLogoError(e) {
      e.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.match-card {
  position: relative;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  transition: background var(--transition-fast);
}
.match-card:last-child { border-bottom: none; }
.match-card--expanded { background: var(--bg-card-hover); }

.league-header {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 2px 0;
}
.league-name {
  font-size: 13px;
  color: var(--primary-light);
  font-weight: 600;
  letter-spacing: 0.04em;
}
.league-logo {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}
.match-card__favorite-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  margin-top: -6px; /* Adjust for removed header */
}
.match-time {
  font-size: 10px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.match-date {
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 700;
  margin-right: 4px;
}
.match-time-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
}

/* Body row */
.match-card__body {
  display: flex;
  align-items: center;
  padding: 8px 12px 4px;
  cursor: pointer;
  user-select: none;
  gap: 8px;
}
.match-card__body:hover .match-card__teams {
  opacity: 0.85;
}

/* Star button */
.btn-star {
  flex: 0 0 auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--text-muted);
  transition: var(--transition);
  border-radius: 50%;
}
.btn-star:hover { color: var(--gold); transform: scale(1.15); }
.btn-star--active { color: var(--gold); }
.btn-star:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.star-icon { width: 18px; height: 18px; }

/* Popular indicator */
.popular-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 4px;
  color: #FF8C00;
}
.star-icon--popular {
  color: #FF8C00;
  width: 18px;
  height: 18px;
}
.favorite-count {
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

/* Teams section */
.match-card__teams {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
}

.team {
  display: flex;
  align-items: center;
  gap: 8px;
}
.team--home {
  justify-content: flex-end;
  text-align: right;
}
.team--away {
  justify-content: flex-start;
  text-align: left;
}

.team__info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.team__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}
.team__rank {
  font-size: 11px;
  color: var(--text-secondary);
}

/* Score / VS */
.match-card__score {
  flex: 0 0 auto;
  text-align: center;
  min-width: 70px;
}
.vs-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-secondary);
  letter-spacing: 0.05em;
}
.score-full {
  font-size: 16px;
  font-weight: 800;
  color: var(--score-full);
  line-height: 1.1;
}
.score-half {
  font-size: 11px;
  color: var(--score-half);
  margin-top: 1px;
}

/* Expand arrow */
.expand-arrow {
  flex: 0 0 auto;
  color: var(--text-muted);
  transition: var(--transition);
}
.expand-arrow svg { width: 16px; height: 16px; display: block; }
.expand-arrow--open { transform: rotate(180deg); color: var(--primary-light); }

/* Odds preview row */
.match-card__odds-preview {
  grid-column: 1 / -1;
  display: flex;
  align-items: flex-end; /* Align bottom to values row */
  justify-content: center;
  gap: 4px;
  padding-bottom: 4px;
}
.odds-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.odds-group__header {
  display: flex;
  gap: 5px;
  opacity: 0.7;
  margin-bottom: 1px;
}
.odds-header-label {
  font-size: 10px;
  color: var(--text-muted);
  /* width: 38px; */
  text-align: center;
  white-space: nowrap;
}
.odds-preview__item {
  display: flex;
  align-items: center;
  gap: 5px;
}
.odds-value, .odds-line {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  /* width: 38px; */
  text-align: center;
}
.odds-line {
  color: var(--handicap);
  font-weight: 600;
}
.odds-divider {
  color: var(--border-strong);
  font-size: 12px;
  margin: 0 4px;
}

/* Expanded odds */
.match-card__odds-expanded {
  overflow: hidden;
}

/* Bet Corner UI */
.match-card-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 2;
}

.match-status-text {
  font-size: 11px;
  font-weight: bold;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.05);
  padding: 3px 8px;
  border-radius: 4px;
}

.btn-bet-corner {
  background: #FF8C00;
  color: #fff;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
.btn-bet-corner:hover {
  background: #FFA500;
}
.btn-bet-corner--active {
  background: var(--text-muted);
}
.btn-bet-corner--active:hover {
  background: var(--text-secondary);
}

.bet-result-corner {
  position: absolute;
  top: 6px;
  right: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 2;
  font-size: 10px;
  pointer-events: none;
}
.bet-head {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.status-text {
  font-weight: 800;
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 3px;
  color: #fff;
}
.payout-text {
  font-weight: 800;
  font-size: 13px;
}
.bet-desc {
  color: var(--text-muted);
  margin-top: 2px;
  background: rgba(0,0,0,0.2);
  padding: 1px 4px;
  border-radius: 2px;
}

.bet-status--pending .status-text { background: var(--text-muted); }
.bet-status--pending .payout-text { color: var(--text-muted); }

.bet-status--win .status-text, .bet-status--half_win .status-text { background: var(--score-full); }
.bet-status--win .payout-text, .bet-status--half_win .payout-text { color: var(--score-full); }

.bet-status--lose .status-text, .bet-status--half_lose .status-text { background: #E53935; }
.bet-status--lose .payout-text, .bet-status--half_lose .payout-text { color: #E53935; }

.bet-status--void .status-text { background: var(--text-secondary); }
.bet-status--void .payout-text { color: var(--text-secondary); }

</style>
